import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tourTheme.css";
import { useAuth } from "../components/AuthProvider";
import { BIOBANK_ROUTES } from "../constants/routes";
import { BIOBANK_TOUR_STEPS } from "./tourSteps";

const TourContext = createContext({
  startBiobankTour: () => {},
  isTourActive: () => false,
});

const STORAGE_PREFIX = "biobankTourCompleted:";
const AUTO_START_DELAY_MS = 900;
const ELEMENT_WAIT_TIMEOUT_MS = 6000;
const ELEMENT_WAIT_INTERVAL_MS = 120;

const waitForElement = (selector, timeout = ELEMENT_WAIT_TIMEOUT_MS) =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - start >= timeout) {
        resolve(null);
        return;
      }
      setTimeout(tick, ELEMENT_WAIT_INTERVAL_MS);
    };
    tick();
  });

export const TourProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const driverRef = useRef(null);
  const stepsRef = useRef([]);
  const runTokenRef = useRef(0);
  const autoStartAttemptedRef = useRef(false);

  const storageKey = `${STORAGE_PREFIX}${user?.id || "anonymous"}`;

  const markCompleted = useCallback(() => {
    try {
      localStorage.setItem(storageKey, "true");
    } catch (e) {
    }
  }, [storageKey]);

  const goToStep = useCallback(
    async (index, { moveDriverTo } = {}) => {
      const steps = stepsRef.current;
      const step = steps[index];
      if (!step) return false;

      const myToken = runTokenRef.current;
      const stillCurrent = () => runTokenRef.current === myToken && !!driverRef.current;

      if (step.route && window.location.pathname !== step.route) {
        navigate(step.route);
      }

      if (step.clickBefore) {
        const trigger = await waitForElement(step.clickBefore, ELEMENT_WAIT_TIMEOUT_MS);
        if (!stillCurrent()) return false;
        if (trigger) {
          trigger.click();

          await new Promise((r) => setTimeout(r, 150));
        }
      }

      const el = await waitForElement(step.selector);

      if (!stillCurrent()) return false;

      if (!el) {
     
        return false;
      }

      if (moveDriverTo) moveDriverTo(index);
      return true;
    },
    [navigate]
  );

  const stopTour = useCallback(() => {
    runTokenRef.current += 1;
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, []);

  const startBiobankTour = useCallback(
    async (steps = BIOBANK_TOUR_STEPS) => {
      if (!steps || steps.length === 0) return;
      if (driverRef.current) return; // already running

      stepsRef.current = steps;
      const myToken = ++runTokenRef.current;

      const driverObj = driver({
        showProgress: true,
        allowClose: true,
        smoothScroll: true,
        animate: true,
        overlayColor: "rgba(20, 8, 6, 0.55)",
        stagePadding: 6,
        stageRadius: 10,
        popoverClass: "biobank-tour-popover",
        progressText: "{{current}} of {{total}}",
        nextBtnText: "Next →",
        prevBtnText: "← Back",
        doneBtnText: "Done",
        steps: steps.map((s) => ({
          element: s.selector,
          popover: { side: s.side, align: s.align, ...s.popover },
        })),

        onNextClick: async (_element, _step, opts) => {
          if (runTokenRef.current !== myToken) return;
          const nextIndex = (opts.index ?? 0) + 1;
          if (nextIndex >= stepsRef.current.length) {
            opts.driver.destroy();
            return;
          }
          const moved = await goToStep(nextIndex, {
            moveDriverTo: (i) => opts.driver.moveTo(i),
          });
          if (!moved && runTokenRef.current === myToken) {
            // Target missing — skip forward past it rather than getting stuck.
            const following = nextIndex + 1;
            if (following < stepsRef.current.length) {
              await goToStep(following, {
                moveDriverTo: (i) => opts.driver.moveTo(i),
              });
            } else {
              opts.driver.destroy();
            }
          }
        },

        onPrevClick: async (_element, _step, opts) => {
          if (runTokenRef.current !== myToken) return;
          const prevIndex = (opts.index ?? 0) - 1;
          if (prevIndex < 0) return;
          await goToStep(prevIndex, {
            moveDriverTo: (i) => opts.driver.moveTo(i),
          });
        },

        onCloseClick: (_element, _step, opts) => {
          opts.driver.destroy();
        },

        onDoneClick: (_element, _step, opts) => {
          opts.driver.destroy();
        },

        onDestroyed: () => {
          markCompleted();
          driverRef.current = null;
        },
      });

      driverRef.current = driverObj;

      const startedOnFirstStep = await goToStep(0, {
        moveDriverTo: () => driverObj.drive(0),
      });
      if (!startedOnFirstStep && runTokenRef.current === myToken) {
        // Even the very first target never appeared — abandon quietly.
        driverObj.destroy();
      }
    },
    [goToStep, markCompleted]
  );

  const isTourActive = useCallback(() => !!driverRef.current, []);


  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname !== BIOBANK_ROUTES.DASHBOARD) return;
    if (autoStartAttemptedRef.current) return;

    let alreadySeen = true;
    try {
      alreadySeen = !!localStorage.getItem(storageKey);
    } catch (e) {
      alreadySeen = true; // no localStorage — don't auto-start repeatedly
    }
    if (alreadySeen) return;

    autoStartAttemptedRef.current = true;
    const timer = setTimeout(() => {
      startBiobankTour(BIOBANK_TOUR_STEPS);
    }, AUTO_START_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAuthenticated, location.pathname, storageKey, startBiobankTour]);

  useEffect(() => stopTour, [stopTour]);

  return (
    <TourContext.Provider value={{ startBiobankTour, stopTour, isTourActive }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);

export default TourProvider;
